/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import type { ShopperProducts } from '@/scapi';
import ImageGallery from '@/components/image-gallery';
import ProductInfo from './product-info';
import ProductCartActions from '@/components/product-cart-actions';
import ProductViewProvider from '@/providers/product-view';
import { useProductImages } from '@/hooks/product/use-product-images';
import { useSelectedVariations } from '@/hooks/product/use-selected-variations';
import { isProductSet, isProductBundle } from '@/lib/product/product-utils';
import CollapsibleHtmlSection from '@/components/collapsible-section/collapsible-html-section';
import { useTranslation } from 'react-i18next';
import { UITarget } from '@/targets/ui-target';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { AppConfig } from '@/types/config';
import { ProductBadge } from '@/components/product-badges/product-badges';

interface ProductViewProps {
    product: ShopperProducts.schemas['Product'];
    mode?: 'add' | 'edit';
}

/**
 * ProductView component renders a complete product detail view with image gallery and product information.
 *
 * @param props - The component props
 * @param props.product - The product data from Salesforce Commerce Cloud containing all product details,
 *                        variants, pricing, and metadata
 *
 * @returns A React element containing the complete product view layout
 *
 * @example
 * ```tsx
 * <ProductView product={productData} />
 * ```
 */
export default function ProductView({ product }: ProductViewProps): ReactElement {
    // Calculate directly without useMemo since these are simple operations
    const isProductASet = isProductSet(product);
    const isProductABundle = isProductBundle(product);

    // Get selected attributes from URL parameters for image gallery
    const selectedAttributes = useSelectedVariations({ product });
    const { galleryImages } = useProductImages({
        product,
        selectedAttributes,
    });

    const { t } = useTranslation('product');

    // "NEW" badge on the main gallery image, driven by the same badge config used on PLP tiles
    // (config.global.badges), read directly off the full product detail shape's custom attribute.
    const config = useConfig<AppConfig>();
    const newBadgeConfig = config.global.badges?.find((badge) => badge.propertyName === 'c_isNew');
    const isNewProduct = Boolean(
        newBadgeConfig && (product as unknown as Record<string, unknown>)[newBadgeConfig.propertyName]
    );
    const newBadge =
        isNewProduct && newBadgeConfig ? (
            <ProductBadge variant="success">{newBadgeConfig.label}</ProductBadge>
        ) : undefined;

    return (
        <ProductViewProvider product={product} mode="add">
            {/*
                Mobile: single column, `order-*` stacks gallery -> info (header/price/variants/actions)
                -> accordions -> standalone size-chart link.
                Desktop (`lg+`): gallery and info sit side by side (auto-placed into row 1, columns 1/2);
                accordions are explicitly pinned under the gallery (col 1, row 2); the standalone mobile
                size-chart block is hidden since desktop shows it inline in ProductInfo's quantity row.
            */}
            <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-4 lg:gap-x-12 lg:gap-y-6">
                {/* Gallery */}
                <div className="order-1 lg:order-none">
                    <ImageGallery
                        key={product.id}
                        images={galleryImages}
                        eager={!isProductASet && !isProductABundle}
                        showNavigationArrows
                        navigationArrowSize="lg"
                        verticalThumbnails
                        badge={newBadge}
                        productName={product.name}
                    />
                    <UITarget targetId="sfcc.pdp.agent.productHelper" />
                </div>

                {/* Info: header, price, variants, actions */}
                <div className="order-2 lg:order-none">
                    <ProductInfo product={product} />
                    <ProductCartActions product={product} />
                    {/* @sfdc-extension-block-start SFDC_EXT_SHIPPING_DELIVERY */}
                    {/* @sfdc-extension-block-end SFDC_EXT_SHIPPING_DELIVERY */}
                    <UITarget targetId="sfcc.pdp.faq" />
                </div>

                {/* Accordions: Description + Product Details, each its own rounded pdp-variant card */}
                <div className="order-3 lg:order-none lg:col-start-1 lg:row-start-2 space-y-3">
                    {product.longDescription && product.longDescription !== product.shortDescription && (
                        <CollapsibleHtmlSection
                            label={`${t('description')}:`}
                            content={product.longDescription}
                            contentType="plain-text"
                            variant="pdp"
                        />
                    )}
                    <UITarget targetId="sfcc.pdp.collapsibles" />
                </div>

                {/* Mobile-only standalone "Size Chart" link, below the accordions */}
                <div className="order-4 lg:hidden">
                    <UITarget targetId="sfcc.pdp.sizeChart" />
                </div>
            </div>
        </ProductViewProvider>
    );
}
